import { createFileRoute } from '@tanstack/react-router';
import { isAirAdmin } from '@airreading/common';
import {
  availabilityOverviewSearchSchema,
  AvailabilityOverviewDay,
  AvailabilityOverviewDistrict,
  AvailabilityOverviewSeasonTotal,
  AvailabilityOverviewState,
  AvailabilityOverviewTimeslot,
  BackgroundCheckStatusLabelMap,
  ClassRatio,
  classRatioSchema,
  CoachAvailabilityType,
  CoachStatus,
  CoachSubStatus,
  coachStatusSchema,
  DayOfWeekLabelMap,
  GetAvailabilityOverviewResults,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ArrowTooltip } from '@/components/arrow-tooltip.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { InfoPage } from '@/components/info-page.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterDropdownSubItems } from '@/components/table/table-filter-dropdown-sub-items.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableFilterTimeRangePicker } from '@/components/table/table-filter-time-range-picker.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';
import { getAvailabilityOverview, getAvailabilityOverviewDistricts } from '@/lib/api-calls.js';
import {
  classEligibilityOptions,
  classRatioOptions,
  availabilityOverviewTimeSlotOptions,
  coachStatusGroupedOptions,
  getCoachOverallGradeOptions,
  teachingDaysOptions,
} from '@/lib/coach-utils.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { booleanFilterToSelectedValues, selectedValuesToBooleanFilter, usePaginatedFilters } from '@/lib/filtering.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { validateRouteRole } from '@/lib/routing.js';
import { coachesSectionTabs } from '../-components/tabs.js';
import { type AvailabilityOverviewDistrictsExport } from './-components/availability-overview-csv.js';
import {
  AvailabilityOverviewDownloadCsv,
  formatAvailabilityTimeslotRange,
} from './-components/availability-overview-download-csv.js';
import { getTeacherSupplyHighlightClass, type TeacherSupplyMetrics } from './-components/teacher-supply-highlight.js';
import { TeacherStateFilterDropdown } from '../-components/teacher-state-filter-dropdown.js';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/teachers/availability-overview/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: AvailabilityOverview,
  validateSearch: paginationRequestSchema.extend({
    ...availabilityOverviewSearchSchema.shape,
    googleMeetTechCheck: z.coerce.boolean().optional(),
    statuses: z.array(coachStatusSchema).default([CoachStatus.ready_to_teach]),
    classRatio: classRatioSchema.default(ClassRatio['1:2']),
  }),
});
const timeslotColumnHelper = createColumnHelper<AvailabilityOverviewTimeslot>();

const teacherSupplyHighlightMeta = {
  getCellClassName: (row: { original: TeacherSupplyMetrics }) =>
    getTeacherSupplyHighlightClass(row.original.coachCount, row.original.teacherDemandForecast),
};

const timeslotColumns = [
  timeslotColumnHelper.accessor('startPlainTime', {
    id: 'timeslot',
    header: 'Timeslots',
    enableSorting: false,
    cell: ({ getValue }) => formatAvailabilityTimeslotRange(getValue()),
  }),
  timeslotColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  timeslotColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  timeslotColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
  timeslotColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
];

const dayColumnHelper = createColumnHelper<AvailabilityOverviewDay>();

const dayColumns = [
  dayColumnHelper.accessor('day', {
    id: 'day',
    header: 'Days of the week',
    enableSorting: false,
    cell: ({ getValue }) => DayOfWeekLabelMap[getValue()].abbr,
  }),
  dayColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  dayColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  dayColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
  dayColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
];

const stateColumnHelper = createColumnHelper<AvailabilityOverviewState>();

const stateColumns = [
  stateColumnHelper.accessor('state', {
    id: 'state',
    header: 'States',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  stateColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
];

const seasonTotalColumnHelper = createColumnHelper<AvailabilityOverviewSeasonTotal>();

const seasonTotalColumns = [
  seasonTotalColumnHelper.accessor('cycle', {
    id: 'cycle',
    header: 'Cycles',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  seasonTotalColumnHelper.accessor('studentCount', {
    id: 'studentCount',
    header: 'Total students',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  seasonTotalColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
  seasonTotalColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
];

const districtColumnHelper = createColumnHelper<AvailabilityOverviewDistrict>();

const districtColumns = [
  districtColumnHelper.accessor('district', {
    id: 'district',
    header: 'District',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('studentSeats', {
    id: 'studentSeats',
    header: 'Student seats',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('enrolledStudentsNeedSchedule', {
    id: 'enrolledStudentsNeedSchedule',
    header: 'Enrolled students need schedule',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
  }),
  districtColumnHelper.accessor('teacherDemandForecast', {
    id: 'teacherDemandForecast',
    header: 'Teacher demand forecast',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
  districtColumnHelper.accessor('coachCount', {
    id: 'coachCount',
    header: 'Available teachers',
    enableSorting: true,
    cell: ({ getValue }) => getValue(),
    meta: teacherSupplyHighlightMeta,
  }),
];

const AvailabilityOverviewTimeslotsCard = ({
  timeslots,
  isLoading,
  hasError,
  refetch,
}: {
  timeslots: AvailabilityOverviewTimeslot[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: timeslots,
    columns: timeslotColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by time slot"
      isLoading={isLoading}
      contentClass="block"
      data-testid="availability-overview-timeslots"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        scrollContainerClassName="max-h-[420px]"
        emptyState="No time slots found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-timeslots-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewDaysCard = ({
  daysOfWeek,
  isLoading,
  hasError,
  refetch,
}: {
  daysOfWeek: AvailabilityOverviewDay[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: daysOfWeek,
    columns: dayColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by class days"
      isLoading={isLoading}
      contentClass="block"
      data-testid="availability-overview-days"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        emptyState="No days found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-days-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewBackgroundChecksCard = ({
  backgroundCheckStatuses,
  isLoading,
}: Pick<GetAvailabilityOverviewResults, 'backgroundCheckStatuses'> & { isLoading: boolean }) => {
  return (
    <InfoCard
      headerText="Available teachers per background check"
      isLoading={isLoading}
      maxItemsPerRow={4}
      data-testid="availability-overview-background-checks"
    >
      {backgroundCheckStatuses.map((item) => (
        <InfoKeyValue
          key={item.status}
          label={BackgroundCheckStatusLabelMap[item.status]}
          value={item.coachCount}
          data-testid={`availability-overview-background-check-${item.status}`}
        />
      ))}
    </InfoCard>
  );
};

const AvailabilityOverviewDistrictsCard = ({
  districts,
  isLoading,
  hasError,
  refetch,
}: {
  districts: AvailabilityOverviewDistrict[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'enrolledStudentsNeedSchedule', desc: true }]);

  const table = useReactTable({
    data: districts,
    columns: districtColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Availability by districts"
      isLoading={isLoading}
      contentClass="block"
      data-testid="availability-overview-districts"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        scrollContainerClassName="max-h-[420px]"
        emptyState="No districts found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-districts-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewStatesCard = ({
  states,
  isLoading,
  hasError,
  refetch,
}: {
  states: AvailabilityOverviewState[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}) => {
  const table = useReactTable({
    data: states,
    columns: stateColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <InfoCard
      headerText="Available teachers by states"
      isLoading={isLoading}
      className="mb-0 h-full"
      contentClass="block"
      data-testid="availability-overview-states"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        hasError={hasError}
        scrollContainerClassName="max-h-[420px]"
        emptyState="No states found"
        emptyStateClassName="translate-y-0"
        errorState={<TableErrorState refetchAction={refetch} />}
        data-testid="availability-overview-states-table"
      />
    </InfoCard>
  );
};

const AvailabilityOverviewSeasonTotalCard = ({
  seasonTotals,
  isLoading,
}: {
  seasonTotals: AvailabilityOverviewSeasonTotal[];
  isLoading: boolean;
}) => {
  const table = useReactTable({
    data: seasonTotals,
    columns: seasonTotalColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoCard
      headerText={
        <span className="flex items-center gap-2">
          Season total
          <ArrowTooltip trigger={<Info className="h-5 w-5 m-1" />} position="right">
            Filters don't apply to this table
          </ArrowTooltip>
        </span>
      }
      isLoading={isLoading}
      className="mb-0 h-full"
      contentClass="block"
      data-testid="availability-overview-season-total"
    >
      <EntityTable
        table={table}
        isLoading={isLoading}
        emptyState="No cycles found"
        emptyStateClassName="translate-y-0"
        data-testid="availability-overview-season-total-table"
        hideEndMessage
      />
    </InfoCard>
  );
};

function AvailabilityOverview() {
  const {
    resetFilters,
    activeFiltersCount,
    filters: {
      statuses: [[statuses, activeStatuses], setStatuses],
      subStatuses: [[subStatuses, activeSubStatuses], setSubStatuses],
      overallGrades: [[overallGrades, activeOverallGrades], setOverallGrades],
      classEligibilities: [[classEligibilities, activeClassEligibilities], setClassEligibilities],
      teachingDays: [[teachingDays, activeTeachingDays], setTeachingDays],
      timeRanges: [[timeRanges], setTimeRanges],
      untakenTeachersOnly: [[untakenTeachersOnly], setUntakenTeachersOnly],
      coachAvailabilityType: [[coachAvailabilityType], setCoachAvailabilityType],
      googleMeetTechCheck: [[googleMeetTechCheck, activeGoogleMeetTechCheck], setGoogleMeetTechCheck],
      startOnOrAfter: [[startOnOrAfter], setStartOnOrAfter],
      endOnOrBefore: [[endOnOrBefore], setEndOnOrBefore],
      classRatio: [[classRatio, activeClassRatio], setClassRatio],
      timeSlots: [[timeSlots, activeTimeSlots], setTimeSlots],
      states: [[states, activeStates], setStates],
    },
  } = usePaginatedFilters(
    Route.id,
    [
      'statuses',
      'subStatuses',
      'overallGrades',
      'classEligibilities',
      'teachingDays',
      'timeRanges',
      'untakenTeachersOnly',
      'coachAvailabilityType',
      'googleMeetTechCheck',
      'startOnOrAfter',
      'endOnOrBefore',
      'classRatio',
      'timeSlots',
      'states',
    ],
    ['overallGrades', 'googleMeetTechCheck', 'classRatio', 'timeSlots', 'states'],
  );

  const queryModifier = useMemo(
    () => ({
      filters: {
        ...(activeStatuses?.length ? { statuses: activeStatuses } : {}),
        ...(activeSubStatuses?.length ? { subStatuses: activeSubStatuses } : {}),
        ...(activeOverallGrades?.length ? { overallGrades: activeOverallGrades } : {}),
        ...(activeClassEligibilities?.length ? { classEligibilities: activeClassEligibilities } : {}),
        ...(activeTeachingDays?.length ? { teachingDays: activeTeachingDays } : {}),
        ...(timeRanges?.length
          ? {
              timeRanges,
              untakenTeachersOnly: untakenTeachersOnly ?? true,
              coachAvailabilityType: coachAvailabilityType ?? CoachAvailabilityType.regular,
            }
          : {}),
        googleMeetTechCheck: activeGoogleMeetTechCheck ?? googleMeetTechCheck,
        ...(startOnOrAfter ? { startOnOrAfter } : {}),
        ...(endOnOrBefore ? { endOnOrBefore } : {}),
        ...(activeClassRatio ? { classRatio: activeClassRatio } : {}),
        ...(activeTimeSlots?.length ? { timeSlots: activeTimeSlots } : {}),
        ...(activeStates?.length ? { states: activeStates } : {}),
      },
    }),
    [
      activeStatuses,
      activeSubStatuses,
      activeOverallGrades,
      activeClassEligibilities,
      activeTeachingDays,
      timeRanges,
      untakenTeachersOnly,
      coachAvailabilityType,
      googleMeetTechCheck,
      activeGoogleMeetTechCheck,
      startOnOrAfter,
      endOnOrBefore,
      activeClassRatio,
      activeTimeSlots,
      activeStates,
    ],
  );
  const { data, isPending, error, refetch } = useQuery(getAvailabilityOverview(queryModifier));
  const {
    data: districtsData,
    isPending: isDistrictsPending,
    error: districtsError,
    refetch: refetchDistricts,
  } = useQuery(getAvailabilityOverviewDistricts(queryModifier));

  useErrorToast(error, 'Error loading availability overview. Please try again.');
  useErrorToast(districtsError, 'Error loading availability by districts. Please try again.');

  const timeslots = data?.timeslots ?? [];
  const daysOfWeek = data?.daysOfWeek ?? [];
  const backgroundCheckStatuses = data?.backgroundCheckStatuses ?? [];
  const districts = districtsData?.districts ?? [];
  const coachStates = data?.states ?? [];
  const seasonTotals = data?.seasonTotals ?? [];
  const hasError = !!error;
  const hasDistrictsError = !!districtsError;
  let districtsExport: AvailabilityOverviewDistrictsExport = 'ready';
  if (hasDistrictsError) {
    districtsExport = 'error';
  } else if (isDistrictsPending) {
    districtsExport = 'pending';
  }

  const filterComponents = (
    <div className="flex flex-wrap items-center gap-2 pb-4 pt-3">
      <TableFilterDropdownSubItems<CoachStatus, CoachSubStatus>
        title="Status"
        options={coachStatusGroupedOptions}
        selectedValues={statuses ?? []}
        selectedSubValues={subStatuses ?? []}
        onChange={setStatuses}
        onSubValuesChange={setSubStatuses}
      />
      <TableFilterPlainDateRangePicker
        fromPlainDateString={startOnOrAfter}
        toPlainDateString={endOnOrBefore}
        onRangeSelect={(range) => {
          setStartOnOrAfter(range.fromPlainDateString);
          setEndOnOrBefore(range.toPlainDateString);
        }}
        placeholder="Date range"
      />
      <TableFilterDropdown
        title="Teaching days"
        options={teachingDaysOptions}
        selectedValues={teachingDays ?? []}
        onChange={setTeachingDays}
      />
      <TableFilterTimeRangePicker
        placeholder="Time range"
        selectedRanges={timeRanges ?? []}
        onChange={setTimeRanges}
        untakenTeachersOnly={untakenTeachersOnly ?? true}
        onUntakenTeachersOnlyChange={setUntakenTeachersOnly}
        coachAvailabilityType={coachAvailabilityType ?? CoachAvailabilityType.regular}
        onCoachAvailabilityTypeChange={setCoachAvailabilityType}
      />
      <TableFilterDropdown
        title="Class Eligibility"
        options={classEligibilityOptions}
        selectedValues={classEligibilities ?? []}
        onChange={setClassEligibilities}
      />
      <TableMoreFiltersMenu
        activeFiltersCount={activeFiltersCount}
        filterItems={[
          {
            title: 'Google Meet Tech Check',
            filterComponent: (
              <TableFilterDropdown
                title="Google Meet Tech Check"
                options={buildBooleanSelectOptions()}
                variant="radio"
                selectedValues={booleanFilterToSelectedValues(googleMeetTechCheck)}
                onChange={(vals) => setGoogleMeetTechCheck(selectedValuesToBooleanFilter(vals))}
              />
            ),
          },
          {
            title: 'Time slots',
            filterComponent: (
              <TableFilterDropdown
                title="Time slots"
                options={availabilityOverviewTimeSlotOptions}
                selectedValues={timeSlots ?? []}
                onChange={setTimeSlots}
              />
            ),
          },
          {
            title: 'Overall Grade',
            filterComponent: (
              <TableFilterDropdown
                title="Overall Grade"
                options={getCoachOverallGradeOptions()}
                selectedValues={overallGrades ?? []}
                onChange={setOverallGrades}
              />
            ),
          },
          {
            title: 'Class ratio',
            filterComponent: (
              <TableFilterDropdown
                title="Class ratio"
                options={classRatioOptions}
                variant="radio"
                selectedValues={classRatio ? [classRatio] : []}
                onChange={(vals) => setClassRatio(vals[0])}
              />
            ),
          },
          {
            title: 'State',
            filterComponent: <TeacherStateFilterDropdown selectedValues={states} onChange={setStates} />,
          },
        ]}
      />
      <TableFilterResetButton show={true} onClick={resetFilters} />
      <AvailabilityOverviewDownloadCsv
        data={{
          timeslots,
          daysOfWeek,
          backgroundCheckStatuses,
          districts,
          states: coachStates,
          seasonTotals,
        }}
        disabled={isPending || hasError}
        districtsExport={districtsExport}
      />
    </div>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      {filterComponents}
      <AvailabilityOverviewTimeslotsCard
        timeslots={timeslots}
        isLoading={isPending}
        hasError={hasError}
        refetch={refetch}
      />
      <AvailabilityOverviewDaysCard
        daysOfWeek={daysOfWeek}
        isLoading={isPending}
        hasError={hasError}
        refetch={refetch}
      />
      <AvailabilityOverviewBackgroundChecksCard
        backgroundCheckStatuses={backgroundCheckStatuses}
        isLoading={isPending}
      />
      <AvailabilityOverviewDistrictsCard
        districts={districts}
        isLoading={isDistrictsPending}
        hasError={hasDistrictsError}
        refetch={refetchDistricts}
      />

      <div className="grid grid-cols-1 gap-5 pb-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <AvailabilityOverviewStatesCard
          states={coachStates}
          isLoading={isPending}
          hasError={hasError}
          refetch={refetch}
        />
        <div>
          <AvailabilityOverviewSeasonTotalCard seasonTotals={seasonTotals} isLoading={isPending} />
        </div>
      </div>
    </InfoPage>
  );
}
